/**
 * Subscription footnote shown beneath the consent checkbox on /checkout
 * when `paymentType === 'recurring'`.
 *
 * Country-aware (mirrors the legacy PaymentForm footnote block):
 *   - CA: bilingual EN/FR — description + "How to cancel?" line for each,
 *         with the info icon.
 *   - UK: description only — no info icon, no cancel instructions.
 *   - Anywhere else: description + "How to cancel?" line, with the info icon.
 *
 * Unlike SubscriptionConsent (which is hidden for UK), the footnote IS shown
 * for UK — only the info icon and cancel-instructions line are suppressed.
 */

import React from 'react';
import { Box, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { COUNTRY_CODES, SUBSCRIPTION_CONTENT, UI_CONFIG } from '../../utils/constants';

export interface SubscriptionFootnoteProps {
  country: string;
}

const textSx = {
  color: UI_CONFIG.SECONDARY_TEXT_COLOR,
  fontSize: '0.87rem',
  textAlign: 'justify',
  textJustify: 'inter-word',
} as const;

const SubscriptionFootnote: React.FC<SubscriptionFootnoteProps> = ({ country }) => {
  const upper = country?.toUpperCase();
  const isUK = upper === COUNTRY_CODES.UK;

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 2, gap: 1 }}>
      {!isUK && <InfoOutlinedIcon sx={{ color: UI_CONFIG.SECONDARY_TEXT_COLOR, fontSize: 20 }} />}
      <Box sx={{ flex: 1 }}>
        {upper === COUNTRY_CODES.CA ? (
          <>
            <Typography sx={textSx}>
              <b>(ENG)</b> {SUBSCRIPTION_CONTENT.FOOTNOTE_DESCRIPTION}
            </Typography>
            <Typography sx={{ ...textSx, mt: 1 }}>
              <b>{SUBSCRIPTION_CONTENT.FOOTNOTE_TITLE}</b>{' '}
              {SUBSCRIPTION_CONTENT.CANCEL_INSTRUCTIONS_PREFIX}{' '}
              <b>{SUBSCRIPTION_CONTENT.MODIFY_TEXT}</b> or <b>{SUBSCRIPTION_CONTENT.CANCEL_TEXT}</b>{' '}
              {SUBSCRIPTION_CONTENT.CANCEL_INSTRUCTIONS_SUFFIX}
            </Typography>
            <Typography sx={{ ...textSx, mt: 1 }}>
              <b>(FRA)</b> {SUBSCRIPTION_CONTENT.FOOTNOTE_DESCRIPTION_FR}
            </Typography>
            <Typography sx={{ ...textSx, mt: 1 }}>
              <b>{SUBSCRIPTION_CONTENT.FOOTNOTE_TITLE_FR}</b>{' '}
              {SUBSCRIPTION_CONTENT.CANCEL_INSTRUCTIONS_PREFIX_FR}{' '}
              <b>{SUBSCRIPTION_CONTENT.MODIFY_TEXT_FR}</b> ou{' '}
              <b>{SUBSCRIPTION_CONTENT.CANCEL_TEXT_FR}</b>{' '}
              {SUBSCRIPTION_CONTENT.CANCEL_INSTRUCTIONS_SUFFIX_FR}
            </Typography>
          </>
        ) : (
          <>
            <Typography sx={textSx}>{SUBSCRIPTION_CONTENT.FOOTNOTE_DESCRIPTION}</Typography>
            {!isUK && (
              <Typography sx={{ ...textSx, mt: 1 }}>
                <b>{SUBSCRIPTION_CONTENT.FOOTNOTE_TITLE}</b>{' '}
                {SUBSCRIPTION_CONTENT.CANCEL_INSTRUCTIONS_PREFIX}{' '}
                <b>{SUBSCRIPTION_CONTENT.MODIFY_TEXT}</b> or{' '}
                <b>{SUBSCRIPTION_CONTENT.CANCEL_TEXT}</b>{' '}
                {SUBSCRIPTION_CONTENT.CANCEL_INSTRUCTIONS_SUFFIX}
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default SubscriptionFootnote;
